import { Tooltip } from '@mui/material';
import { map } from 'es-toolkit/compat';
import React from 'react';

import { ConditionalTextEditor } from 'components/atoms/DataGridRenderers/ConditionalTextEditor';
import { DisplayedField } from 'interfaces/genericFields';
import { ModelType } from 'interfaces/modelType';
import { stainingMethodsList } from 'interfaces/stainType';
import { renderDefaultModelCellSelector } from './DefaultModelCellSelector';

const idRegex = /^[a-z\d/]+(?:[-_][a-z\d/]+)*$/;

export const idField: DisplayedField<ModelType & { draftId?: string }, string> = {
  cellEditType: 'text',
  filterType: 'text',
  dataKey: 'id',
  label: 'Model Type ID',
  columnWidth: { width: 150 },
  getError: ({ value }) =>
    !idRegex.test(value)
      ? 'Model type id must be lowercase and contain only letters, numbers. ' +
        'Hyphens, and underscores are allowed as separators.'
      : undefined,
  customCellEditor:
    (field, context) =>
    ({ isEditable: isEditableParam, ...params }) => {
      const { value, row } = params;
      const isEditable = (model: ModelType & { draftId?: string }) => Boolean(isEditableParam && model.draftId);
      const error = field.getError?.({ context, value, row });
      const showError = isEditable(row) && Boolean(error);
      return (
        <Tooltip open={Boolean(showError)} color="error" title={showError ? error : undefined}>
          <ConditionalTextEditor {...params} isEditable={isEditable} />
        </Tooltip>
      );
    },
};

export const displayNameField: DisplayedField<ModelType, string> = {
  cellEditType: 'text',
  filterType: 'text',
  dataKey: 'displayName',
  label: 'Display Name',
  columnWidth: { width: 200 },
};

export const typeField: DisplayedField<ModelType, string> = {
  cellEditType: 'text',
  filterType: 'text',
  dataKey: 'type',
  label: 'Type',
  columnWidth: { width: 150 },
};

export const trainingTypeField: DisplayedField<ModelType, string, { trainingTypes: string[] }> = {
  display: 'flex',
  cellEditType: 'select',
  filterType: 'select',
  optionsGetter: (context) => {
    return map(context?.trainingTypes, (value) => ({
      value,
      label: value,
    }));
  },
  dataKey: 'trainingType',
  label: 'Training Type',
  columnWidth: { width: 150 },
};

export const backgroundClassesToIgnoreField: DisplayedField<ModelType, string> = {
  cellEditType: 'multiText',
  filterType: 'multiText',
  dataKey: 'backgroundClassesToIgnore',
  label: 'Background Classes To Ignore',
  columnWidth: { width: 200 },
};

export const stainingMethodField: DisplayedField<ModelType, string> = {
  cellEditType: 'select',
  filterType: 'select',
  optionsGetter: () => map(stainingMethodsList, (value) => ({ value, label: value })),
  dataKey: 'stainingMethod',
  label: 'Staining Method',
  columnWidth: { width: 150 },
};

export const defaultModelUrlField: DisplayedField<ModelType, string> = {
  cellEditType: 'text',
  filterType: 'text',
  dataKey: 'defaultModelUrl',
  label: 'Default Model URL',
  columnWidth: { width: 150 },
  customCellEditor: () => renderDefaultModelCellSelector,
};

export const modelTypeFields: Array<DisplayedField<ModelType, any>> = [
  idField,
  displayNameField,
  typeField,
  trainingTypeField,
  backgroundClassesToIgnoreField,
  stainingMethodField,
  defaultModelUrlField,
];
